import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";

interface UseInlineRenameOptions {
  initialValue: string;
  onSave: (newValue: string) => Promise<void>;
  onCancel?: () => void;
  maxLength?: number;
}

/**
 * Generic hook for inline rename inputs.
 *
 * Enter → save (if changed and non-empty), else silent revert.
 * Escape → cancel without save.
 * Blur → same rule as Enter.
 *
 * The parent owns visibility (input shown or not) and error toasts.
 * `onSave` should throw on failure — the hook will propagate but keep
 * the input mounted so the parent can decide to close or retry.
 *
 * WHY silent-revert on empty: users reaching for backspace-all-then-Enter
 * are signalling "never mind" rather than "set title to empty string".
 * Showing a validation error here is friction with zero upside.
 */
export function useInlineRename({
  initialValue,
  onSave,
  onCancel,
  maxLength = 255,
}: UseInlineRenameOptions) {
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus + select all on mount — ChatGPT/Claude convention: the existing
  // title is pre-selected so the user can overtype without Ctrl+A first.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const cancel = useCallback(() => {
    setValue(initialValue);
    onCancel?.();
  }, [initialValue, onCancel]);

  const commit = useCallback(async () => {
    const trimmed = value.trim().slice(0, maxLength);
    if (!trimmed || trimmed === initialValue) {
      cancel();
      return;
    }
    try {
      setIsSaving(true);
      await onSave(trimmed);
    } finally {
      setIsSaving(false);
    }
  }, [value, initialValue, maxLength, onSave, cancel]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void commit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    },
    [commit, cancel],
  );

  const handleBlur = useCallback(() => {
    void commit();
  }, [commit]);

  return {
    value,
    setValue,
    isSaving,
    inputRef,
    handleKeyDown,
    handleBlur,
    commit,
    cancel,
  };
}
